function toNumber(value, fallback = 0) {
  if (value === null || typeof value === 'undefined' || value === '') return fallback;
  const n = Number(String(value).replace(/[^0-9.-]/g, ''));
  return Number.isFinite(n) ? n : fallback;
}

function hasNumber(value) {
  if (value === null || typeof value === 'undefined' || value === '') return false;
  return Number.isFinite(Number(String(value).replace(/[^0-9.-]/g, '')));
}

function safeParse(value, fallback = null) {
  if (!value) return fallback;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(String(value));
  } catch (_) {
    return fallback;
  }
}

function titleFromScope(value = '') {
  const clean = String(value || 'ALL').replace(/^inventory_/i, '').replace(/[_-]+/g, ' ').trim();
  if (!clean || clean.toUpperCase() === 'ALL') return 'All Sites';
  return clean.replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeSiteId(value = '') {
  const clean = String(value || 'ALL').trim();
  return clean || 'ALL';
}

function isAggregateSite(siteId = '', siteName = '') {
  const a = String(siteId || '').trim().toLowerCase();
  const b = String(siteName || '').trim().toLowerCase();
  return !a || a === 'all' || a === 'all sites' || a === 'all stores' || b === 'all sites' || b === 'all stores';
}

function docTime(data = {}, parsed = {}) {
  return String(
    parsed.syncedAt ||
    parsed.synced_at ||
    parsed.savedAt ||
    parsed.updatedAt ||
    parsed.archivedAt ||
    parsed.completedAt ||
    data?.updatedAt?.toDate?.()?.toISOString?.() ||
    data?.createdAt?.toDate?.()?.toISOString?.() ||
    ''
  );
}

function normalizeSessionEngineDoc(docId = '', data = {}, parsed = {}, localKey = '') {
  const meta = parsed?.meta && typeof parsed.meta === 'object' ? parsed.meta : {};
  const lines = Array.isArray(parsed?.lines) ? parsed.lines : [];
  if (!lines.length) return null;

  const status = String(meta.status || parsed.status || '').toLowerCase();
  const archivedAt = meta.archivedAt || parsed.archivedAt || data?.archivedAt;
  const isArchived = Boolean(archivedAt) || status === 'archived' || status === 'completed';
  const completedLines = lines.filter((line) => line && (hasNumber(line.counted) || hasNumber(line.waste) || String(line.note || line.notes || '').trim())).length;
  const isCompletedCount = status === 'completed' || String(meta.archiveType || parsed.archiveType || '').toLowerCase() === 'completed' || (lines.length > 0 && completedLines >= lines.length);
  if (!isArchived || !isCompletedCount) return null;

  const siteId = normalizeSiteId(meta.storeKey || meta.siteId || meta.store || meta.storeName || 'ALL');
  const siteName = String(meta.storeName || meta.store || titleFromScope(siteId)).trim() || titleFromScope(siteId);
  const syncedAt = String(meta.archivedAt || parsed.archivedAt || parsed.savedAt || meta.updatedAt || meta.completedAt || docTime(data, parsed) || '');
  const sessionId = String(meta.sessionId || parsed.sessionId || localKey.split(':').pop() || docId || '').trim();

  return {
    id: docId,
    localKey,
    siteId,
    siteName,
    source: 'morpheus-count',
    provider: 'morpheus',
    syncedAt,
    rowCount: lines.length,
    authority: 'counted',
    sessionId,
    aggregate: isAggregateSite(siteId, siteName),
    rows: lines.map((line, index) => {
      const productName = String(line.productName || line.product || line.name || '').trim();
      const countedSoh = hasNumber(line.counted) ? toNumber(line.counted, 0) : toNumber(line.systemSoh ?? line.realSoh ?? line.soh, 0);
      return {
        id: String(line.id || line.sku || `${siteId}-${sessionId}-${index + 1}`),
        siteId,
        siteName,
        sku: String(line.sku || line.variant_sku || line.code || '').trim(),
        productName,
        category: String(line.category || line.product_category || 'Other').trim() || 'Other',
        physicalSoh: countedSoh,
        countedSoh,
        waste: toNumber(line.waste, 0),
        systemSohAtCount: toNumber(line.systemSoh ?? line.realSoh ?? line.soh, 0),
        variance: toNumber(line.variance, 0),
        retailPrice: toNumber(line.retailPrice ?? line.retail_price ?? line.price, 0),
        cost: toNumber(line.cost ?? line.supplyPrice ?? line.supply_price, 0),
        source: 'morpheus-count',
        provider: 'morpheus',
        authority: 'counted',
        sessionId,
        syncedAt,
      };
    }).filter((row) => row.productName),
  };
}

export function normalizeMorpheusInventoryDoc(docId = '', data = {}) {
  const localKey = String(data?.localKey || docId || '');
  const parsed = safeParse(data?.json, null) || safeParse(data?.value, null) || data || {};

  const isSessionEngine = /^mc-session-engine[:\-]/i.test(localKey) || /^mc-session-engine[:\-]/i.test(docId) || (Array.isArray(parsed?.lines) && parsed?.meta?.sessionId);
  if (isSessionEngine) return normalizeSessionEngineDoc(docId, data, parsed, localKey);

  const isSnapshot = /mc-link-inventory-snapshot/i.test(localKey) || /link[-_]inventory[-_]snapshot/i.test(docId);
  const isPrepared = /mc-prepared-inventory/i.test(localKey) || /prepared[-_]inventory/i.test(docId);
  if (!isSnapshot && !isPrepared) return null;

  const siteIdFromKey = localKey.includes(':') ? localKey.split(':').slice(1).join(':') : '';
  const siteId = normalizeSiteId(parsed.siteId || siteIdFromKey || 'ALL');
  const siteName = String(parsed.siteName || titleFromScope(siteId)).trim() || titleFromScope(siteId);
  const syncedAt = docTime(data, parsed);
  const source = String(parsed.source || 'morpheus').trim();
  const provider = String(parsed.provider || 'lightspeed').trim();
  const rows = Array.isArray(parsed.rows) ? parsed.rows : [];

  return {
    id: docId,
    localKey,
    siteId,
    siteName,
    source,
    provider,
    syncedAt,
    rowCount: rows.length,
    authority: 'prepared',
    aggregate: isAggregateSite(siteId, siteName),
    rows: rows.map((row, index) => {
      const productName = String(row.productName || row.product || row.name || '').trim();
      return {
        id: String(row.id || row.sku || `${siteId}-${index + 1}`),
        siteId,
        siteName,
        sku: String(row.sku || row.variant_sku || row.code || '').trim(),
        productName,
        category: String(row.category || row.product_category || '').trim(),
        physicalSoh: toNumber(row.countedSoh ?? row.counted ?? row.realSoh ?? row.physicalSoh ?? row.systemSoh ?? row.system_soh ?? row.inventory_Main_Outlet ?? row.soh, 0),
        retailPrice: toNumber(row.retailPrice ?? row.retail_price ?? row.price, 0),
        cost: toNumber(row.cost ?? row.supplyPrice ?? row.supply_price, 0),
        source: String(row.source || source).trim(),
        provider: String(row.provider || provider).trim(),
        authority: 'prepared',
        syncedAt: String(row.syncedAt || row.synced_at || syncedAt || ''),
      };
    }).filter((row) => row.productName),
  };
}

function snapshotPriority(snapshot = {}) {
  return snapshot.authority === 'counted' ? 2 : 1;
}

function timeValue(value = '') {
  const n = Date.parse(value || '');
  return Number.isFinite(n) ? n : 0;
}

export function selectLatestInventorySnapshotsBySite(snapshots = []) {
  const bySite = new Map();
  (Array.isArray(snapshots) ? snapshots : []).forEach((snapshot) => {
    if (!snapshot || snapshot.aggregate || isAggregateSite(snapshot.siteId, snapshot.siteName)) return;
    const siteKey = String(snapshot.siteId || snapshot.siteName || '').trim();
    if (!siteKey) return;
    const current = bySite.get(siteKey);
    if (!current) {
      bySite.set(siteKey, snapshot);
      return;
    }
    const nextPriority = snapshotPriority(snapshot);
    const currentPriority = snapshotPriority(current);
    if (nextPriority > currentPriority) bySite.set(siteKey, snapshot);
    else if (nextPriority === currentPriority && timeValue(snapshot.syncedAt) >= timeValue(current.syncedAt)) bySite.set(siteKey, snapshot);
  });
  return Array.from(bySite.values()).sort((a, b) => String(a.siteName).localeCompare(String(b.siteName)));
}

export function flattenInventorySnapshots(snapshots = []) {
  return selectLatestInventorySnapshotsBySite(snapshots).flatMap((snapshot) => Array.isArray(snapshot?.rows) ? snapshot.rows : []);
}

export function getInventoryFreshness(syncedAt = '') {
  const time = Date.parse(syncedAt || '');
  if (!Number.isFinite(time)) return { label: 'No sync time', tone: 'slate', minutes: null };
  const minutes = Math.max(0, Math.round((Date.now() - time) / 60000));
  if (minutes <= 15) return { label: `${minutes}m ago`, tone: 'emerald', minutes };
  if (minutes <= 60) return { label: `${minutes}m ago`, tone: 'amber', minutes };
  return { label: `${Math.round(minutes / 60)}h ago`, tone: 'rose', minutes };
}
